import { useTranslations } from '@vocab/react';
import { useState } from 'react';
import { flushSync } from 'react-dom';
import { useFormContext } from 'react-hook-form';

import { VirusScanStatus } from 'src/__generated__/graphql';
import { PageSection, useTrackingClient } from 'src/context/tracking';
import { uploadCoverLetter } from 'src/context/upload-attachments';
import {
  AttachmentOptionFile,
  AttachmentOptionText,
  AttachmentSelect,
  FileAttachment,
} from 'src/forms/common/AttachmentSelect/AttachmentSelect';
import {
  MAX_CHARS,
  useValidators,
} from 'src/forms/common/AttachmentSelect/hooks/useValidators';
import { useGetCurrentPage } from 'src/hooks/useGetCurrentPage';
import { useVirusScan } from 'src/hooks/useVirusScan';
import { isFile } from 'src/utils';

import translations from './.vocab';

export interface CoverLetterFieldsValues {
  'coverLetter-method': FileAttachment;
  'coverLetter-uri': string;
  'coverLetter-file': File;
  'coverLetter-text': string;
  'coverLetter-virusScan': unknown;
}

interface Props {
  defaultMethod?: FileAttachment;
  defaultValue?: string | File;
  hideTitle?: boolean;
}

export function CoverLetterFields({
  defaultMethod,
  defaultValue,
  hideTitle = false,
}: Props) {
  const { t } = useTranslations(translations);
  const { trackDontIncludeCoverletter } = useTracking();

  const [isLoading, setIsLoading] = useState(false);

  const { watch, setValue, trigger, formState, setError, clearErrors } =
    useFormContext<CoverLetterFieldsValues>();

  const { validateMethod, validateFile, validateText } = useValidators({
    currentMethod: watch('coverLetter-method') || defaultMethod,
  });

  useVirusScan({
    fileUri: watch('coverLetter-uri'),
    currentMethod: watch('coverLetter-method'),
    onStatusChange: (virusScanStatus, method) => {
      const shouldCheck = method === FileAttachment.UPLOAD;

      if (!shouldCheck) {
        clearErrors('coverLetter-virusScan');
      } else if (virusScanStatus === VirusScanStatus.HasVirus) {
        setError('coverLetter-virusScan', { message: t('Virus Detected') });
      } else {
        clearErrors('coverLetter-virusScan');
      }

      setIsLoading(virusScanStatus === VirusScanStatus.Pending);
    },
  });

  const handleCoverLetterChange = async (coverLetter?: File) => {
    if (!coverLetter) return;

    try {
      setIsLoading(true);
      const uri = await uploadCoverLetter({ coverLetter });
      flushSync(() => setValue('coverLetter-uri', uri));
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      // handle error
    } finally {
      trigger('coverLetter-file'); // trigger validation
    }
  };
  return (
    <AttachmentSelect
      hideTitle={hideTitle}
      title={t('Cover letter')}
      name="coverLetter-method"
      labelNone={t('Dont include a cover letter')}
      defaultValue={defaultMethod}
      onChange={(method) => {
        if (method === FileAttachment.NONE) {
          trackDontIncludeCoverletter();
        }
      }}
      validate={validateMethod}
    >
      <AttachmentOptionFile
        testid="coverLetterFileInput"
        name="coverLetter-file"
        label={t('Upload a cover letter')}
        description={t('Accepted file types')}
        reUploadText={t('Upload a different cover letter')}
        defaultValue={isFile(defaultValue) ? defaultValue : undefined}
        validate={validateFile}
        onChange={handleCoverLetterChange}
        isLoading={isLoading}
        message={formState.errors['coverLetter-virusScan']?.message}
      />
      <AttachmentOptionText
        testid="coverLetterTextInput"
        name="coverLetter-text"
        label={t('Write a cover letter')}
        description={t('Introduce yourself')}
        defaultValue={
          typeof defaultValue === 'string' ? defaultValue : undefined
        }
        validate={validateText}
        limit={MAX_CHARS}
      />
    </AttachmentSelect>
  );
}

function useTracking() {
  const currentPage = useGetCurrentPage();
  const props = { currentPage };
  const client = useTrackingClient();

  function trackDontIncludeCoverletter() {
    client.trackLink({
      ...props,
      link: 'cover_letter_not_included_pressed',
      eventName: 'cover_letter_not_included_pressed',
      currentPageSection: PageSection.COVER_LETTER,
    });
  }

  return {
    trackDontIncludeCoverletter,
  };
}
